import {
  Checkbox,
  ChoiceGroup,
  Dropdown,
  mergeStyleSets,
} from "@fluentui/react";
import DatePickerField from "components/controls/DatePickerField";
import NumberInputField from "components/controls/NumberInputField";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import {
  ASSISTANCE_OPTION_ERROR_KEY,
  COVERAGE_DATE_LINES,
  COVERAGE_FIELDS,
  CoverageDateKey,
  CoverageDateLine,
  Coverages,
  coverageDateErrorKey,
  coverageDateKeys,
  HullVariant,
  isRequestedLineAvailable,
  LINE_OPTIONS,
  lineOptionErrorKey,
  LineOptionsGetters,
  parseCoverageDate,
  parseCoverages,
  parseRequestedLines,
  REQUESTED_LINE_FIELDS,
  RequestedLines as RequestedLinesValue,
  resolveAssistanceOptions,
  WINDSCREEN_LIMIT_ERROR_KEY,
  WINDSCREEN_SCOPE_ERROR_KEY,
  WINDSCREEN_SCOPES,
  WindscreenScope,
  serializeClientCoverages,
  serializeCoverageDate,
} from "./requestedLinesHelpers";

interface Props {
  /** Current clientCoveragesInfo JSON string. */
  value?: string | null;
  /** Formik setFieldValue; writes the serialized JSON to clientCoveragesInfo. */
  setFieldValue: (name: string, value: any) => void;
  disabled?: boolean;
  /** CZ: coverage from / to dates for the requested MTPL and Hull. */
  showCoverageDates?: boolean;
  /** Formik errors (coverage date errors are keyed by coverageDateErrorKey). */
  errors?: Record<string, any>;
  /** The external user changed the requested lines (yellow, like the other marked fields). */
  isMarked?: boolean;
  /** Additional hull coverages offered to the client (shown at the end when Hull is requested). */
  offeredCoverages?: (keyof Coverages)[];
  /** Insurance lines the client can tick (default: all). */
  lineFields?: typeof REQUESTED_LINE_FIELDS;
  /**
   * CZ: per line, the options the client can pick for the line requested from
   * the given coverage start (MTPL insurance limits, hull deductible options);
   * when set, a drop-down is shown next to the line's coverage dates (if there
   * are options for that day).
   */
  getLineOptions?: LineOptionsGetters;
  /**
   * CZ: per line, the assistance options of the line's main insurer for its
   * coverage start; when set and Assistance is requested, an assistance
   * drop-down is shown in one row (see resolveAssistanceOptions).
   */
  getAssistanceOptions?: LineOptionsGetters;
  /** CZ: windscreen coverage (scope + limit) offered with MTPL or Hull. */
  showWindscreen?: boolean;
}

const getClassNames = (theme: IStyledTheme, isMarked: boolean) =>
  mergeStyleSets({
    container: {
      width: "100%",
      border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
      borderRadius: "2px",
      padding: "12px 16px",
      marginBottom: "8px",
      ...(isMarked && {
        background: theme.palette.yellowLight,
        selectors: {
          // keep the labels readable on yellow in the dark theme
          ".ms-Checkbox-text, .ms-ChoiceFieldLabel, .ms-Label": {
            color: "black !important",
          },
        },
      }),
    },
    label: {
      fontWeight: 600,
      fontSize: "14px",
      marginBottom: "8px",
      color: isMarked ? "black" : theme.palette.black,
    },
    row: {
      display: "flex",
      flexWrap: "wrap",
      gap: "20px",
    },
    dateRow: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "flex-start",
      gap: "20px",
      marginTop: "12px",
    },
    dateRowLabel: {
      width: "60px",
      paddingTop: "32px",
      fontWeight: 600,
      fontSize: "14px",
    },
    dateField: {
      width: "220px",
    },
    windscreenCheckbox: {
      paddingTop: "32px",
    },
    // fits "10%/min: 5000 CZK/max: 50000 CZK"
    lineOptionField: {
      width: "300px",
    },
  });

const RequestedLines: React.FC<Props> = ({
  value,
  setFieldValue,
  disabled,
  showCoverageDates,
  errors,
  isMarked = false,
  offeredCoverages,
  lineFields = REQUESTED_LINE_FIELDS,
  getLineOptions,
  getAssistanceOptions,
  showWindscreen,
}) => {
  const { t } = useTranslation();
  const theme = useTheme() as IStyledTheme;
  const classes = getClassNames(theme, isMarked);
  const lines = parseRequestedLines(value);
  // Preserve the additional coverages half of the blob when we rewrite lines.
  const coverages = parseCoverages(value);

  // Stable Date objects: DatePickerField writes its value back whenever the
  // default value changes by reference.
  const dates = useMemo(() => {
    const l = parseRequestedLines(value);
    const result: Partial<Record<CoverageDateKey, Date | undefined>> = {};
    COVERAGE_DATE_LINES.forEach((line) => {
      const { from, to } = coverageDateKeys(line);
      result[from] = parseCoverageDate(l[from]);
      result[to] = parseCoverageDate(l[to]);
    });
    return result;
  }, [value]);

  // A picked option (MTPL limit, hull deductible, assistance) that is no longer
  // offered after a change (line unticked, other coverage start) is dropped.
  const dropUnofferedOptions = (updated: RequestedLinesValue) => {
    COVERAGE_DATE_LINES.forEach((line) => {
      const optionKey = LINE_OPTIONS[line].key;
      const getOptions = getLineOptions?.[line];
      const from = updated[coverageDateKeys(line).from];
      if (
        updated[optionKey] &&
        getOptions &&
        !(updated[line] && from && getOptions(from).includes(updated[optionKey]!))
      ) {
        delete updated[optionKey];
      }
    });
    if (
      updated.assistanceOption &&
      getAssistanceOptions &&
      !resolveAssistanceOptions(updated, getAssistanceOptions)?.options.includes(
        updated.assistanceOption
      )
    ) {
      delete updated.assistanceOption;
    }
  };

  const toggle = (key: string, checked: boolean) => {
    if (disabled) return;
    const updated = { ...lines, [key]: checked };
    // An unticked line keeps no coverage period.
    if (!checked && (COVERAGE_DATE_LINES as readonly string[]).includes(key)) {
      const { from, to } = coverageDateKeys(key as any);
      delete updated[from];
      delete updated[to];
      delete updated[LINE_OPTIONS[key as CoverageDateLine].key];
    }
    // Assistance goes with the last of MTPL / Hull
    if (!isRequestedLineAvailable("assistance", updated)) {
      updated.assistance = false;
    }
    if (!updated.assistance) delete updated.assistanceOption;
    // the windscreen coverage goes with the last of MTPL / Hull
    if (!updated.mtpl && !updated.hull) updated.windscreen = false;
    if (!updated.windscreen) {
      delete updated.windscreenScope;
      delete updated.windscreenLimit;
    }
    dropUnofferedOptions(updated);
    setFieldValue("clientCoveragesInfo", serializeClientCoverages(updated, coverages));
  };

  // Additional hull coverages: no backend fields, stored in the same JSON.
  const toggleCoverage = (key: keyof Coverages, checked: boolean) => {
    if (disabled) return;
    setFieldValue(
      "clientCoveragesInfo",
      serializeClientCoverages(lines, { ...coverages, [key]: checked })
    );
  };

  const setDate = (key: CoverageDateKey, date?: Date | null) => {
    if (disabled) return;
    const serialized = serializeCoverageDate(date);
    if (serialized === lines[key]) return; // no change (also DatePickerField's write-back)
    const updated = { ...lines };
    if (serialized) updated[key] = serialized;
    else delete updated[key];
    dropUnofferedOptions(updated);
    setFieldValue("clientCoveragesInfo", serializeClientCoverages(updated, coverages));
  };

  const setLineOption = (line: CoverageDateLine, value?: string) => {
    if (disabled) return;
    const updated = { ...lines };
    const optionKey = LINE_OPTIONS[line].key;
    if (value) updated[optionKey] = value;
    else delete updated[optionKey];
    setFieldValue("clientCoveragesInfo", serializeClientCoverages(updated, coverages));
  };

  // CZ: the options offered for a requested line's coverage start
  const lineOptions = (line: CoverageDateLine): string[] => {
    const from = lines[coverageDateKeys(line).from];
    const getOptions = getLineOptions?.[line];
    return getOptions && lines[line] && from ? getOptions(from) : [];
  };

  // CZ: the requested Assistance's options and the row showing them
  const assistance = resolveAssistanceOptions(lines, getAssistanceOptions);

  const setAssistanceOption = (value?: string) => {
    if (disabled) return;
    const updated = { ...lines };
    if (value) updated.assistanceOption = value;
    else delete updated.assistanceOption;
    setFieldValue("clientCoveragesInfo", serializeClientCoverages(updated, coverages));
  };

  // CZ: windscreen scope / limit ("" = not set)
  const setWindscreen = (
    changes: { windscreenScope?: WindscreenScope; windscreenLimit?: string }
  ) => {
    if (disabled) return;
    const updated = { ...lines };
    if ("windscreenScope" in changes) {
      if (changes.windscreenScope) updated.windscreenScope = changes.windscreenScope;
      else delete updated.windscreenScope;
    }
    if ("windscreenLimit" in changes) {
      const limit = Number.parseInt(changes.windscreenLimit ?? "", 10);
      if (Number.isNaN(limit)) delete updated.windscreenLimit;
      else updated.windscreenLimit = limit;
    }
    if (
      updated.windscreenScope === lines.windscreenScope &&
      updated.windscreenLimit === lines.windscreenLimit
    ) {
      return; // no change (also NumberInputField's write-back)
    }
    setFieldValue("clientCoveragesInfo", serializeClientCoverages(updated, coverages));
  };

  const setVariant = (variant?: HullVariant) => {
    if (disabled) return;
    setFieldValue(
      "clientCoveragesInfo",
      serializeClientCoverages({ ...lines, hullVariant: variant ?? null }, coverages)
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.label}>{t("bfm.vehicleForm.requestedLines.label")}</div>
      <div className={classes.row}>
        {lineFields
          .filter((f) => isRequestedLineAvailable(f.key, lines))
          .map((f) => (
            <Checkbox
              key={f.key}
              label={t(f.labelKey)}
              checked={!!lines[f.key]}
              disabled={disabled}
              onChange={(_, checked) => toggle(f.key, !!checked)}
            />
          ))}
      </div>
      {showCoverageDates &&
        COVERAGE_DATE_LINES.filter((line) => lines[line]).map((line) => {
          const { from, to } = coverageDateKeys(line);
          const labelKey = REQUESTED_LINE_FIELDS.find((f) => f.key === line)?.labelKey;
          const lineOption = LINE_OPTIONS[line];
          const options = lineOptions(line);
          return (
            <div key={line} className={classes.dateRow}>
              <div className={classes.dateRowLabel}>{labelKey ? t(labelKey) : line}</div>
              <div className={classes.dateField}>
                <DatePickerField
                  name={coverageDateErrorKey(from)}
                  label="bfm.CoverageFrom.label"
                  defaultValue={dates[from]}
                  setFieldValue={(_, date) => setDate(from, date)}
                  onChange={() => {}}
                  errors={errors ?? {}}
                  disabled={disabled}
                  placeholder={t("bfm.selectDate")}
                  isMarked={isMarked}
                />
              </div>
              <div className={classes.dateField}>
                <DatePickerField
                  name={coverageDateErrorKey(to)}
                  label="bfm.CoverageTo.label"
                  defaultValue={dates[to]}
                  setFieldValue={(_, date) => setDate(to, date)}
                  onChange={() => {}}
                  errors={errors ?? {}}
                  disabled={disabled}
                  placeholder={t("bfm.selectDate")}
                  isMarked={isMarked}
                />
              </div>
              {options.length > 0 && (
                <div className={classes.lineOptionField}>
                  <Dropdown
                    id={lineOptionErrorKey(lineOption.key)}
                    label={t(lineOption.labelKey)}
                    required
                    placeholder={t(lineOption.placeholderKey)}
                    options={options.map((option) => ({
                      key: option,
                      text: option,
                    }))}
                    selectedKey={lines[lineOption.key] ?? null}
                    onChange={(_, option) =>
                      setLineOption(line, option?.key as string)
                    }
                    disabled={disabled}
                    errorMessage={errors?.[lineOptionErrorKey(lineOption.key)]}
                  />
                </div>
              )}
              {assistance?.line === line && (
                <div className={classes.dateField}>
                  <Dropdown
                    id={ASSISTANCE_OPTION_ERROR_KEY}
                    label={t("bfm.vehicleForm.assistanceOption.label")}
                    required
                    placeholder={t("bfm.vehicleForm.assistanceOption.placeholder")}
                    options={assistance.options.map((option) => ({
                      key: option,
                      text: option,
                    }))}
                    selectedKey={lines.assistanceOption ?? null}
                    onChange={(_, option) =>
                      setAssistanceOption(option?.key as string)
                    }
                    disabled={disabled}
                    errorMessage={errors?.[ASSISTANCE_OPTION_ERROR_KEY]}
                  />
                </div>
              )}
            </div>
          );
        })}
      {showWindscreen && (lines.mtpl || lines.hull) && (
        <div className={classes.dateRow}>
          <div className={classes.windscreenCheckbox}>
            <Checkbox
              label={t("bfm.vehicleForm.windscreen.label")}
              checked={!!lines.windscreen}
              disabled={disabled}
              onChange={(_, checked) => toggle("windscreen", !!checked)}
            />
          </div>
          {lines.windscreen && (
            <>
              <div className={classes.dateField}>
                <Dropdown
                  id={WINDSCREEN_SCOPE_ERROR_KEY}
                  label={t("bfm.vehicleForm.windscreenScope.label")}
                  required
                  placeholder={t("bfm.vehicleForm.windscreenScope.placeholder")}
                  options={WINDSCREEN_SCOPES.map((s) => ({
                    key: s.key,
                    text: t(s.labelKey),
                  }))}
                  selectedKey={lines.windscreenScope ?? null}
                  onChange={(_, option) =>
                    setWindscreen({
                      windscreenScope: option?.key as WindscreenScope,
                    })
                  }
                  disabled={disabled}
                  errorMessage={errors?.[WINDSCREEN_SCOPE_ERROR_KEY]}
                />
              </div>
              <div className={classes.dateField}>
                <NumberInputField
                  name={WINDSCREEN_LIMIT_ERROR_KEY}
                  label="bfm.vehicleForm.windscreenLimit.label"
                  required
                  value={
                    lines.windscreenLimit != null
                      ? String(lines.windscreenLimit)
                      : ""
                  }
                  setFieldValue={(_, value) =>
                    setWindscreen({ windscreenLimit: value })
                  }
                  errors={errors ?? {}}
                  disabled={disabled}
                  isMarked={isMarked}
                />
              </div>
            </>
          )}
        </div>
      )}
      {lines.hull && (
        <div style={{ marginTop: 12 }}>
          <ChoiceGroup
            label={t("bfm.vehicleForm.hullVariant.label")}
            required
            selectedKey={lines.hullVariant || undefined}
            disabled={disabled}
            options={[
              {
                key: "TEILKASKO",
                text: t("bfm.vehicleForm.hullVariant.teilkasko"),
              },
              {
                key: "VOLLKASKO",
                text: t("bfm.vehicleForm.hullVariant.vollkasko"),
              },
            ]}
            onChange={(_, opt) => setVariant(opt?.key as HullVariant)}
            styles={{ flexContainer: { display: "flex", gap: 15 } }}
          />
        </div>
      )}
      {lines.hull && offeredCoverages && offeredCoverages.length > 0 && (
        <div className={classes.row} style={{ marginTop: 12 }}>
          {COVERAGE_FIELDS.filter((f) => offeredCoverages.includes(f.key)).map(
            (f) => (
              <Checkbox
                key={f.key}
                label={t(f.labelKey)}
                checked={!!coverages[f.key]}
                disabled={disabled}
                onChange={(_, checked) => toggleCoverage(f.key, !!checked)}
              />
            )
          )}
        </div>
      )}
    </div>
  );
};

export default RequestedLines;
